import { getPanelCornerPoints } from '../../panels/helpers';
import { Island } from '../island.class';
import { nearestSixteenth } from '../../../helpers';
import { Size } from '../../../types';

/**
 * Calculates island baseSize based on island type. Base size represents the outside dimensions and do not include countertop overhangs.
 * Corner islands skip nearestSixteenth rounding because their 45-degree panels produce
 * irrational √2 corner point coordinates — rounding those cascades errors to dimensions.
 * @param island Island to calculate the base size for
 * @returns Size
 */
export const getIslandSizeCfn = (island: Island): Size => {
	let xMin = 0;
	let xMax = 0;
	let yMin = 0;
	let yMax = 0;
	let zMin = 0;
	let zMax = 0;

	island.panels.children.forEach((p) => {
		// Check BOTH inside and outside faces to capture the true outer extent
		const cpOutside = getPanelCornerPoints(p, { inside: false, translate: true, rotate: true, nearestSixteenth: false, lock: false }, new Set());
		const cpInside = getPanelCornerPoints(p, { inside: true, translate: true, rotate: true, nearestSixteenth: false, lock: false }, new Set());

		[...cpOutside.corners, ...cpInside.corners].forEach((corner) => {
			xMin = Math.min(xMin, corner.x);
			xMax = Math.max(xMax, corner.x);
			yMin = Math.min(yMin, corner.y);
			yMax = Math.max(yMax, corner.y);
			zMin = Math.min(zMin, corner.z);
			zMax = Math.max(zMax, corner.z);
		});
	});

	const width = xMax - xMin;
	const height = yMax - yMin;
	const depth = zMax - zMin;

	// Corner islands: skip rounding — the irrational √2 front panel contaminates
	// all corner point coordinates; rounding them introduces 1/16" errors.
	// Standard islands: round to nearest 1/16" for clean dimensions.
	return island.isCornerIsland
		? { width, height, depth }
		: { width: nearestSixteenth(width), height: nearestSixteenth(height), depth: nearestSixteenth(depth) };
};
